import React from 'react';
import ProfessionalSurveyResults from '../../components/survey/ProfessionalSurveyResults';
import { SurveyResults, LegacyTableData } from '../../components/survey/types';

// Note: Interfaces moved to ../../components/survey/types.ts for better organization

interface EnhancedSurveyResultsProps {
  data: SurveyResults | { tables: LegacyTableData[] };
  contentType?: string;
  isExporting?: boolean;
  images?: string[];
  questions?: any[];
  onImageClick?: (src: string, alt: string) => void;
  surveyName?: string;
  surveyGoal?: string;
  surveyContext?: string;
}

const EnhancedSurveyResults: React.FC<EnhancedSurveyResultsProps> = ({
  data,
  contentType,
  isExporting = false,
  images = [],
  questions = [],
  onImageClick,
  surveyName,
  surveyGoal,
  surveyContext,
}) => {
  // Merge question images with survey data
  let enhancedData = data;

  // Check if this is the enhanced survey format and we have questions to merge
  if ('survey_results' in data && questions.length > 0) {
    const surveyData = data as SurveyResults;

    // Create a new data object with merged question images
    enhancedData = {
      ...surveyData,
      survey_results: {
        ...surveyData.survey_results,
        questions: surveyData.survey_results.questions.map((question, index) => {
          // Find matching question from contentData using multiple strategies
          // Strategy 1: Match by ID (most reliable for questions with same text)
          let matchingQuestion = questions.find(q =>
            q.id && question.question_id &&
            q.id === question.question_id
          );

          // Strategy 2: If no ID match, use index position (reliable when question order is preserved)
          if (!matchingQuestion && index < questions.length) {
            const questionAtIndex = questions[index];
            // Verify it's a reasonable match by checking if IDs exist but don't match
            // If IDs don't exist on either side, or text roughly matches, use index
            if (!questionAtIndex.id || !question.question_id ||
                (questionAtIndex.text && question.question_text &&
                 questionAtIndex.text.toLowerCase().trim() === question.question_text.toLowerCase().trim())) {
              matchingQuestion = questionAtIndex;
            }
          }

          // Strategy 3: Fallback to text matching for legacy data without IDs
          if (!matchingQuestion) {
            matchingQuestion = questions.find(q =>
              q.text && question.question_text &&
              q.text.toLowerCase().trim() === question.question_text.toLowerCase().trim()
            );
          }

          if (matchingQuestion) {
            // Merge image data from contentData question
            return {
              ...question,
              image_paths: matchingQuestion.image_paths || question.image_paths,
              image_descriptions: matchingQuestion.image_descriptions || question.image_descriptions,
              image_attachment_level: matchingQuestion.image_attachment_level || question.image_attachment_level,
              option_images: matchingQuestion.option_images || question.option_images
            };
          }

          return question;
        })
      }
    };
  }

  // Use the new professional survey results component
  return (
    <ProfessionalSurveyResults
      data={enhancedData}
      contentType={contentType}
      isExporting={isExporting}
      images={images}
      onImageClick={onImageClick}
      surveyName={surveyName}
      surveyGoal={surveyGoal}
      surveyContext={surveyContext}
    />
  );
};

export default EnhancedSurveyResults;
